import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { auth, ApiError } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from '../components/auth/AuthCard';

type Status = 'verifying' | 'ok' | 'error';

export function VerifyEmailPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<Status>('verifying');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Verification token missing from the URL.');
      return;
    }
    // Guard against React 18 StrictMode double-invocation eating our single-use token.
    // Important: do NOT cancel the in-flight promise from the cleanup function — the StrictMode
    // dev tear-down would set `cancelled = true` before the API responds, leaving the page
    // permanently stuck on the "verifying" state. The ref alone is enough to dedupe; setState
    // calls on an unmounted component just no-op with a console warning (which is harmless).
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      try {
        await auth.verifyEmail(token);
        // Phase 7-dashboard hotfix round 3 — verification opens the session server-side
        // (cookies were set on the response). Refresh AuthContext so the SPA picks up
        // the signed-in candidate, then route to home rather than /signin.
        await refresh();
        setStatus('ok');
        const redirect = window.setTimeout(() => navigate('/'), 1500);
        return () => window.clearTimeout(redirect);
      } catch (err) {
        setStatus('error');
        if (err instanceof ApiError) {
          if (err.code === 'TOKEN_EXPIRED') {
            setErrorMessage('This verification link has expired. Sign in and request a new one.');
          } else if (err.code === 'TOKEN_ALREADY_CONSUMED') {
            setErrorMessage('This verification link has already been used. You can sign in now.');
          } else {
            setErrorMessage(err.message);
          }
        } else {
          setErrorMessage('Could not verify your email. The link may have expired.');
        }
      }
    })();
  }, [token, navigate, refresh]);

  if (status === 'verifying') {
    return (
      <AuthCard publicTheme eyebrow="Verifying email" title="Confirming your email…">
        <div className="qh-auth-public__loader flex items-center justify-center py-10 text-brand-primary">
          <Loader2 className="w-10 h-10 animate-spin" />
        </div>
      </AuthCard>
    );
  }

  if (status === 'ok') {
    return (
      <AuthCard publicTheme eyebrow="Email verified" title="You&apos;re all set">
        <div className="flex flex-col items-center text-center py-4">
          <div className="qh-auth-public__status-icon qh-auth-public__status-icon--success w-20 h-20 bg-brand-accent/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-brand-accent" />
          </div>
          <p className="text-brand-muted mb-6">
            You&apos;re signed in — taking you home now.
          </p>
          <Link
            to="/"
            className="block w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-brand-deep transition-all"
          >
            Continue
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard publicTheme eyebrow="Verification failed" title="We couldn&apos;t verify your email">
      <div className="flex flex-col items-center text-center py-4">
        <div className="qh-auth-public__status-icon qh-auth-public__status-icon--error w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <XCircle className="w-10 h-10 text-red-700" />
        </div>
        <p className="text-brand-muted mb-6">{errorMessage}</p>
        <Link
          to="/signin"
          className="block w-full py-4 bg-brand-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest text-center hover:bg-brand-deep transition-all"
        >
          Back to sign in
        </Link>
      </div>
    </AuthCard>
  );
}
